'use client';

import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAppStore } from '@/store/app';
import {
  strategyCopy,
  templateName,
  skipReasonLabel,
  tradeReasonLabel,
  type PortfolioBacktestItem,
} from '@/lib/strategy';

interface SingleItem {
  id: string;
  templateKey: string;
  symbol: string | null;
  state: string;
  capital: number;
  createdAt: string;
  totalReturnPct: number | null;
}

interface IntentRow {
  time: number;
  market: string;
  executed: boolean;
  reason: string;
}

export default function SignalAnalysisPage() {
  const { language } = useAppStore();
  const zh = language !== 'en';
  const t = strategyCopy(zh);

  const [mode, setMode] = useState<'single' | 'shared'>('single');
  const [selected, setSelected] = useState<string>('');

  const { data: singles } = useQuery({
    queryKey: ['strategy-backtest-list'],
    queryFn: () => api.get<SingleItem[]>('/strategy/backtest?limit=30'),
  });

  const { data: shareds } = useQuery({
    queryKey: ['strategy-backtest-shared-list'],
    queryFn: () => api.get<PortfolioBacktestItem[]>('/strategy/backtest/shared?limit=30'),
  });

  const list = mode === 'single' ? (singles ?? []) : (shareds ?? []);
  const done = list.filter((x) => x.state === 'succeeded');

  useEffect(() => {
    setSelected('');
  }, [mode]);

  const { data: job, isLoading } = useQuery({
    queryKey: ['strategy-signal-job', mode, selected],
    queryFn: async () => {
      const r = await api.get<any>(mode === 'shared' ? `/strategy/backtest/shared/${selected}` : `/strategy/backtest/${selected}`);
      return r;
    },
    enabled: !!selected,
  });

  const rows = useMemo<IntentRow[]>(() => {
    if (!job || job.state !== 'succeeded' || !job.result) return [];
    const res = job.result;
    const trades = res.trades ?? [];
    const skips = res.skips ?? [];
    const symbol = job.symbol ?? null;
    const out: IntentRow[] = [];
    for (const tr of trades) {
      if (tr.side !== 'buy') continue;
      out.push({ time: tr.time, market: tr.symbol ?? symbol ?? '', executed: true, reason: tr.reason ?? '' });
    }
    for (const sk of skips) {
      out.push({ time: sk.time, market: sk.symbol ?? symbol ?? '', executed: false, reason: sk.reason ?? '' });
    }
    return out.sort((a, b) => a.time - b.time);
  }, [job]);

  const funnel = useMemo(() => {
    const executed = rows.filter((r) => r.executed).length;
    const rejected = rows.filter((r) => !r.executed).length;
    const intents = executed + rejected;
    const rate = intents > 0 ? (executed / intents) * 100 : 0;
    const byReason = new Map<string, number>();
    for (const r of rows) {
      if (r.executed) continue;
      byReason.set(r.reason, (byReason.get(r.reason) ?? 0) + 1);
    }
    return { executed, rejected, intents, rate, byReason };
  }, [rows]);

  const execStateLabel = (executed: boolean) => (zh ? (executed ? '已执行' : '被拒绝') : executed ? 'Executed' : 'Rejected');
  return (
    <div className="mx-auto w-full max-w-[1200px] px-4 pb-24 pt-4">
      <div className="mb-4">
        <h1 className="text-xl font-bold">{t.signalAnalysisTitle}</h1>
        <p className="text-xs text-gf-muted">{t.analysisSubtitle}</p>
      </div>

      {/* 分析对象选择 */}
      <div className="card mb-4 p-4">
        <div className="mb-2 text-sm font-semibold">{t.chooseTask}</div>
        <p className="mb-3 text-xs text-gf-muted">{t.chooseTaskHint}</p>
        <div className="flex flex-col gap-2 md:flex-row md:items-center">
          <div className="flex overflow-hidden rounded-lg border border-gf-border">
            <button
              onClick={() => setMode('single')}
              className={`px-3 py-1.5 text-xs ${mode === 'single' ? 'bg-gf-accent text-white' : 'bg-gf-surface2 text-gf-muted'}`}
            >
              {t.singleBacktests}
            </button>
            <button
              onClick={() => setMode('shared')}
              className={`px-3 py-1.5 text-xs ${mode === 'shared' ? 'bg-gf-accent text-white' : 'bg-gf-surface2 text-gf-muted'}`}
            >
              {t.sharedBacktests}
            </button>
          </div>
          <select
            className="w-full rounded-lg border border-gf-border bg-gf-surface2 px-3 py-1.5 text-sm md:w-96"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
          >
            <option value="">{zh ? '选择任务…' : 'Select a task…'}</option>
            {done.map((x: any) => (
              <option key={x.id} value={x.id}>
                {templateName(x.templateKey, zh)} · {mode === 'shared' ? (x.symbols ?? []).slice(0, 3).join('+') : x.symbol ?? '-'} · {new Date(x.createdAt).toLocaleDateString()}
              </option>
            ))}
          </select>
        </div>
        {done.length === 0 ? (
          <p className="mt-3 text-xs text-gf-muted">{t.noTasks} — {t.noTaskHint}</p>
        ) : null}
      </div>

      {!selected ? (
        <div className="card p-10 text-center text-xs text-gf-muted">{zh ? '请选择分析对象' : 'Select an analysis target'}</div>
      ) : isLoading ? (
        <div className="py-16 text-center text-sm text-gf-muted">{zh ? '载入中…' : 'Loading…'}</div>
      ) : !job || job.state !== 'succeeded' ? (
        <div className="card p-10 text-center text-xs text-gf-muted">{zh ? '任务未完成，暂无信号数据' : 'Task not finished; no signal data yet'}</div>
      ) : rows.length === 0 ? (
        <div className="card p-10 text-center">
          <div className="text-sm font-medium">{t.noIntents}</div>
          <div className="mt-1 text-xs text-gf-muted">{t.noIntentsNote}</div>
        </div>
      ) : (
        <>
          {/* 四统计卡 */}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <div className="card p-4">
              <span className="text-xs text-gf-muted">{t.validIntents}</span>
              <div className="mt-0.5 text-2xl font-semibold">{funnel.intents}</div>
              <span className="text-[11px] text-gf-muted">{zh ? '含已执行与被拒绝' : 'Executed + rejected'}</span>
            </div>
            <div className="card p-4">
              <span className="text-xs text-gf-muted">{t.executed}</span>
              <div className="mt-0.5 text-2xl font-semibold text-gf-positive">{funnel.executed}</div>
              <span className="text-[11px] text-gf-muted">{zh ? '成功入场' : 'Entries filled'}</span>
            </div>
            <div className="card p-4">
              <span className="text-xs text-gf-muted">{t.rejected}</span>
              <div className="mt-0.5 text-2xl font-semibold text-gf-negative">{funnel.rejected}</div>
              <span className="text-[11px] text-gf-muted">{zh ? '风险/预算/数据限制' : 'Risk / budget / data limits'}</span>
            </div>
            <div className="card p-4">
              <span className="text-xs text-gf-muted">{t.execRate}</span>
              <div className="mt-0.5 text-2xl font-semibold">{funnel.rate.toFixed(1)}%</div>
              <span className="text-[11px] text-gf-muted">{zh ? '执行/意向' : 'Executed / intents'}</span>
            </div>
          </div>

          <div className="mt-4 grid gap-4 md:grid-cols-[1fr_280px]">
            {/* 拒绝原因分布 + 明细 */}
            <div className="space-y-4">
              <div className="card p-4">
                <div className="mb-2 text-sm font-semibold">{t.rejectReasonDist}</div>
                {funnel.byReason.size === 0 ? (
                  <p className="text-xs text-gf-muted">{zh ? '无拒绝记录' : 'No rejections'}</p>
                ) : (
                  <ul className="space-y-2">
                    {[...funnel.byReason.entries()].sort((a, b) => b[1] - a[1]).map(([reason, count]) => (
                      <li key={reason} className="flex items-center justify-between text-xs">
                        <span className="text-gf-muted">{skipReasonLabel(zh, reason)}</span>
                        <span className="font-semibold">{count}</span>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="mt-3 border-t border-gf-border pt-2 text-[11px] text-gf-muted">{t.ruleNotTriggeredNote}</p>
                {funnel.rejected > 0 && funnel.executed === 0 ? (
                  <p className="mt-1 text-[11px] text-gf-negative">{t.allRejectedNote}</p>
                ) : null}
              </div>

              <div className="card overflow-hidden !p-0">
                <div className="flex items-center justify-between border-b border-gf-border px-4 py-3">
                  <span className="text-sm font-semibold">{t.intentDetailTitle}</span>
                  <span className="text-[11px] text-gf-muted">{t.detailRows} · {rows.length}</span>
                </div>
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full min-w-[560px] text-sm">
                    <thead>
                      <tr className="border-b border-gf-border text-xs text-gf-muted">
                        <th className="px-4 py-2 text-left">{zh ? '时间' : 'Time'}</th>
                        <th className="px-4 py-2 text-left">{t.signalMarket}</th>
                        <th className="px-4 py-2 text-left">{t.result}</th>
                        <th className="px-4 py-2 text-left">{t.mainReason}</th>
                        <th className="px-4 py-2 text-right">{t.action}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.slice(-60).reverse().map((r, i) => (
                        <tr key={i} className="border-b border-gf-border/50">
                          <td className="px-4 py-2 text-xs text-gf-muted">{new Date(r.time).toLocaleString()}</td>
                          <td className="px-4 py-2 font-medium">{r.market || '—'}</td>
                          <td className={`px-4 py-2 ${r.executed ? 'text-gf-positive' : 'text-gf-negative'}`}>{execStateLabel(r.executed)}</td>
                          <td className="px-4 py-2 text-xs">{r.executed ? tradeReasonLabel(zh, r.reason) : skipReasonLabel(zh, r.reason)}</td>
                          <td className="px-4 py-2 text-right">
                            <div className="flex items-center justify-end gap-3">
                              <Link href={`/strategy/${job.templateKey}?taskId=${selected}`} className="text-xs font-medium text-gf-accent">
                                {t.signalViewReport}
                              </Link>
                              {mode === 'shared' ? (
                                <Link href={`/strategy/portfolios/${selected}/analysis`} className="text-xs font-medium text-gf-accent">
                                  {t.signalViewEvidence} →
                                </Link>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* 手机：明细卡片 */}
                <ul className="divide-y divide-gf-border/60 md:hidden">
                  {rows.slice(-30).reverse().map((r, i) => (
                    <li key={i} className="px-4 py-3">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{r.market || '—'}</span>
                        <span className={`text-xs ${r.executed ? 'text-gf-positive' : 'text-gf-negative'}`}>{execStateLabel(r.executed)}</span>
                      </div>
                      <div className="mt-1 text-xs text-gf-muted">{r.executed ? tradeReasonLabel(zh, r.reason) : skipReasonLabel(zh, r.reason)}</div>
                      <div className="mt-1 flex items-center justify-between gap-2 text-[11px] text-gf-muted">
                        <span>{new Date(r.time).toLocaleString()}</span>
                        <span className="flex shrink-0 items-center gap-2">
                          <Link href={`/strategy/${job.templateKey}?taskId=${selected}`} className="font-medium text-gf-accent">{t.signalViewReport}</Link>
                          {mode === 'shared' ? (
                            <Link href={`/strategy/portfolios/${selected}/analysis`} className="font-medium text-gf-accent">{t.signalViewEvidence} →</Link>
                          ) : null}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
                <div className="border-t border-gf-border px-4 py-2 text-[11px] text-gf-muted md:hidden">{t.clickForDetail} {t.signalEvidenceNote}</div>
              </div>
            </div>

            {/* 筛选范围卡 */}
            <div className="card h-fit p-4">
              <div className="mb-2 text-sm font-semibold">{t.filterScope}</div>
              <dl className="space-y-2 text-xs">
                <div className="flex justify-between gap-2">
                  <dt className="text-gf-muted">{t.filterTemplate}</dt>
                  <dd className="text-right font-medium">{templateName(job.templateKey, zh)}</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-gf-muted">{mode === 'shared' ? t.signalFilterSymbol : t.filterTime}</dt>
                  <dd className="text-right font-medium">
                    {mode === 'shared'
                      ? (job.symbols ?? []).slice(0, 4).join(' · ')
                      : new Date(job.createdAt).toLocaleDateString()}
                  </dd>
                </div>
                {mode === 'shared' ? (
                  <div className="flex justify-between gap-2">
                    <dt className="text-gf-muted">{t.filterTime}</dt>
                    <dd className="text-right font-medium">{new Date(job.createdAt).toLocaleDateString()}</dd>
                  </div>
                ) : (
                  <div className="flex justify-between gap-2">
                    <dt className="text-gf-muted">{t.filterSymbol}</dt>
                    <dd className="text-right font-medium">{job.symbol ?? '—'}</dd>
                  </div>
                )}
                <div className="flex justify-between gap-2">
                  <dt className="text-gf-muted">{t.filterReasonStatus}</dt>
                  <dd className="text-right font-medium">{zh ? '全部' : 'All'}</dd>
                </div>
              </dl>
              <p className="mt-3 border-t border-gf-border pt-2 text-[11px] text-gf-muted">{t.evidenceNote}</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
